import { Component, computed, inject, input, signal, ViewEncapsulation } from '@angular/core';
import {
    ButtonComponent,
    LabelComponent,
    MenuButtonDirective,
    MenuModule,
    PaginationComponent,
    RootDirective,
    TableModule,
    TooltipDirective,
} from '@pure/hive';
import {
    ContractRenewalRenderInfo,
    RECOMMENDATION_ACTION_EVENT_TOKEN,
    RecommendationActionEvent,
} from '../../../../data/models/recommendations.models';
import { faDownload, faEllipsisV, faPlus, faUpload } from '@fortawesome/pro-regular-svg-icons';
import { Subject } from 'rxjs';
import { registerAsDialogPlugin } from '../../services/recommendation-component-registry.service';

/** Stable registry key for this plugin. Used as the lookup name on the corresponding RenderInfo. */
export const CONTRACT_RENEWAL_DIALOG_PLUGIN_NAME = 'ContractRenewalDialogPluginComponent';

/**
 * Dialog component for contract renewal recommendations.
 *
 * This component is displayed when a user clicks on a contract renewal recommendation card.
 * It shows detailed information about the contract renewal.
 *
 * To create a dialog for a new recommendation type, see: recommendations-container/README.md
 */
@Component({
    selector: 'contract-renewal-dialog-plugin',
    templateUrl: './contract-renewal-dialog-plugin.component.html',
    styleUrls: ['./contract-renewal-dialog-plugin.component.scss'],
    standalone: true,
    imports: [
        LabelComponent,
        ButtonComponent,
        RootDirective,
        TableModule,
        PaginationComponent,
        MenuModule,
        MenuButtonDirective,
        TooltipDirective,
    ],
    encapsulation: ViewEncapsulation.None,
})
export class ContractRenewalDialogPluginComponent {
    renderInfo = input.required<ContractRenewalRenderInfo>();

    readonly faDownload = faDownload;
    readonly faPlus = faPlus;
    readonly faUpload = faUpload;
    readonly PAGE_SIZE_OPTIONS = [10, 25, 50];
    readonly RecommendationActionEvent = RecommendationActionEvent;

    // Inject action events subject
    private readonly recommendationActionEvent$ = inject<Subject<RecommendationActionEvent>>(
        RECOMMENDATION_ACTION_EVENT_TOKEN,
        {
            optional: true,
        },
    );

    // Pagination state
    pageSize = signal(10);
    offset = signal(0);

    /**
     * Get the paginated subset of contract targets based on current offset and page size
     */
    paginatedContractTargets = computed(() => {
        const start = this.offset();
        const end = this.offset() + this.pageSize();
        return this.renderInfo().contractTargets.slice(start, end);
    });

    /**
     * Emits an action event with the specified action type
     */
    handleActionEvent(action: RecommendationActionEvent): void {
        this.recommendationActionEvent$?.next(action);
    }

    /**
     * Handle page change event from pagination
     * @param offset The new offset (starting index) for the page
     */
    onPageChange(offset: number): void {
        this.offset.set(offset);
    }

    /**
     * Handle items per page change event from pagination
     * @param newPageSize The new page size
     */
    onItemsPerPageChange(newPageSize: number): void {
        this.pageSize.set(newPageSize);
        this.offset.set(0); // Reset to first page when page size changes
    }

    protected readonly faEllipsisV = faEllipsisV;
}

registerAsDialogPlugin(CONTRACT_RENEWAL_DIALOG_PLUGIN_NAME, ContractRenewalDialogPluginComponent);
